/**
 * Stable icon URLs from public folder. Use these for nav and map icons
 * so they load reliably on the website and after Capgo OTA updates (no hashed chunk paths).
 *
 * Activity icons live in Supabase Storage as mono SVGs. DB stores the file stem only
 * (e.g. "pickleball" → …/activities/mono/pickleball.svg).
 */
const ACTIVITY_ICON_STORAGE_PREFIX = 'storage/v1/object/public/relentless-assets/activities/mono'

function getBaseUrl(): string {
  return typeof window !== 'undefined' ? window.location.origin : (process.env.PUBLIC_URL || '.')
}

function getSupabaseUrl(): string {
  return (process.env.REACT_APP_SUPABASE_URL ?? '').replace(/\/$/, '')
}

export function getPublicIconUrl(path: string): string {
  const base = getBaseUrl()
  const normalized = path.startsWith('/') ? path : `/${path}`
  return `${base.replace(/\/$/, '')}${normalized}`
}

/** Resolve an activity/bucket icon key (or absolute URL) to a MaskedIcon src. */
export function resolveActivityIconUrl(icon: string): string {
  if (!icon) return ''
  if (icon.startsWith('http://') || icon.startsWith('https://')) return icon
  const file = icon.endsWith('.svg') ? icon : `${icon}.svg`
  const base = getSupabaseUrl()
  return base ? `${base}/${ACTIVITY_ICON_STORAGE_PREFIX}/${file}` : ''
}

/** Paths under public/icons/ (no leading slash). */
export const publicIconPaths = {
  navigation: {
    chat: 'icons/navigation/chat.svg',
    chatSecondary: 'icons/navigation/chat_secondary.svg',
    home: 'icons/navigation/home.svg',
    homeSecondary: 'icons/navigation/home_secondary.svg',
    log: 'icons/navigation/log.svg',
    logSecondary: 'icons/navigation/log_secondary.svg',
    progress: 'icons/navigation/progress.svg',
    progressSecondary: 'icons/navigation/progress_secondary.svg',
    team: 'icons/team.svg',
    teamSecondary: 'icons/navigation/team_secondary.svg',
  },
  map: {
    locationRed: 'icons/map/location_red.svg',
  },
} as const

/** Resolved URLs for nav icons (TabBar, etc.). */
export function getNavIconUrls() {
  return {
    chat: getPublicIconUrl(publicIconPaths.navigation.chat),
    chatSecondary: getPublicIconUrl(publicIconPaths.navigation.chatSecondary),
    home: getPublicIconUrl(publicIconPaths.navigation.home),
    homeSecondary: getPublicIconUrl(publicIconPaths.navigation.homeSecondary),
    log: getPublicIconUrl(publicIconPaths.navigation.log),
    logSecondary: getPublicIconUrl(publicIconPaths.navigation.logSecondary),
    progress: getPublicIconUrl(publicIconPaths.navigation.progress),
    progressSecondary: getPublicIconUrl(publicIconPaths.navigation.progressSecondary),
    team: getPublicIconUrl(publicIconPaths.navigation.team),
    teamSecondary: getPublicIconUrl(publicIconPaths.navigation.teamSecondary),
  }
}

export function getMapIconUrls() {
  return {
    locationRed: getPublicIconUrl(publicIconPaths.map.locationRed),
  }
}
